import { getPodcastsByIds } from '../data/podcastData.js';

export class PodcastPage {
    constructor(containerId, options = {}) {
        this.containerId = containerId;
        this.podcast = options.podcast;
        this.onBack = options.onBack || (() => console.log('Back clicked'));
        
        this.element = null;
        this.audioPlayer = null;
        this.videoPlayer = null;
        this.miniPlayer = null;

    }

    init() {
        this.element = document.getElementById(this.containerId);
        if (!this.element || !this.podcast) {
            console.error('PodcastPage init failed: container or podcast data missing.');
            return;
        }

        this.injectStyles();
        this.render();
        this.attachEventListeners();
        
        // Fade in the page
        setTimeout(() => {
            const view = this.element.querySelector('.podcast-page-view');
            if (view) view.classList.add('visible');
        }, 10);
    }

    injectStyles() {
        const styleId = 'podcast-page-styles';
        if (document.getElementById(styleId)) return;

        const css = `
            :root {
                --pd-primary-blue: #0066cc;
                --pd-bg-main: #f0f2f5;
                --pd-bg-card: #ffffff;
                --pd-text-primary: #050505;
                --pd-text-secondary: #65676b;
                --pd-border-color: #e4e6eb;
            }
            .podcast-page-view {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background-color: var(--pd-bg-main);
                z-index: 2000;
                display: flex;
                flex-direction: column;
                opacity: 0;
                transition: opacity 0.3s ease-in-out;
            }
            .podcast-page-view.visible { opacity: 1; }

            /* Header */
            .pd-header {
                position: sticky;
                top: 0;
                width: 100%;
                height: 60px;
                background-color: rgba(255,255,255,0.8);
                backdrop-filter: blur(10px);
                -webkit-backdrop-filter: blur(10px);
                border-bottom: 1px solid var(--pd-border-color);
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 0 16px;
                z-index: 10;
                flex-shrink: 0;
            }
            .pd-back-btn {
                background: none;
                border: none;
                cursor: pointer;
                padding: 8px;
                color: var(--pd-text-primary);
                display: flex;
                align-items: center;
                gap: 6px;
                font-weight: 600;
                font-size: 16px;
            }
            .pd-header-title {
                font-size: 18px;
                font-weight: 700;
                text-align: center;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                padding: 0 16px;
            }

            /* Scrollable Content */
            .pd-scroll-content {
                flex-grow: 1;
                overflow-y: auto;
                padding-bottom: 80px; /* Space for mini-player */
            }

            /* Hero Section */
            .pd-hero {
                width: 100%;
                height: 50vh;
                min-height: 400px;
                position: relative;
                display: flex;
                flex-direction: column;
                justify-content: flex-end;
                align-items: center;
                padding: 24px;
                color: white;
                text-align: center;
            }
            .pd-hero-image {
                position: absolute;
                top: 0; left: 0; width: 100%; height: 100%;
                object-fit: cover;
                z-index: 1;
            }
            .pd-hero::after { /* Gradient overlay */
                content: '';
                position: absolute;
                bottom: 0; left: 0; width: 100%; height: 70%;
                background: linear-gradient(to top, rgba(0,0,0,0.8) 20%, transparent 100%);
                z-index: 2;
            }
            .pd-hero-buttons, .pd-hero-meta { position: relative; z-index: 3; }
            .pd-hero-buttons { display: flex; gap: 16px; margin-bottom: 16px; }
            .pd-play-btn {
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 14px 24px;
                border-radius: 50px;
                border: none;
                font-size: 18px;
                font-weight: 700;
                cursor: pointer;
                transition: transform 0.2s, background-color 0.2s;
            }
            .pd-play-btn:hover { transform: scale(1.05); }
            .pd-play-btn.primary { background-color: var(--pd-primary-blue); color: white; }
            .pd-play-btn.secondary { background-color: rgba(255,255,255,0.2); color: white; border: 1px solid rgba(255,255,255,0.5); }
            .pd-play-btn.hidden { display: none; }
            .pd-hero-meta { display: flex; gap: 16px; font-size: 14px; opacity: 0.8; }

            /* Content Sections */
            .pd-section {
                background: var(--pd-bg-card);
                margin: 16px;
                border-radius: 12px;
                padding: 20px;
                border: 1px solid var(--pd-border-color);
            }
            .pd-section-title {
                font-size: 20px;
                font-weight: 700;
                margin-bottom: 16px;
            }

            /* Quick Actions */
            .pd-quick-actions {
                display: flex;
                justify-content: space-around;
                padding: 0;
                margin: 16px;
            }
            .pd-action-btn {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 8px;
                font-size: 12px;
                color: var(--pd-text-secondary);
                background: none; border: none; cursor: pointer;
            }
            .pd-action-btn .icon-heart.filled { color: red; }
            .pd-action-btn .icon-heart.filled svg { fill: red; }

            /* Description */
            .pd-description-text {
                line-height: 1.7;
                color: var(--pd-text-primary);
            }
            .pd-description-text.collapsed {
                max-height: 105px; /* Approx 5 lines */
                overflow: hidden;
                position: relative;
            }
            .pd-description-text.collapsed::after {
                content: '';
                position: absolute;
                bottom: 0; left: 0; width: 100%; height: 40px;
                background: linear-gradient(to top, var(--pd-bg-card), transparent);
            }
            .pd-description-toggle {
                background: none; border: none;
                color: var(--pd-primary-blue);
                font-weight: 600;
                cursor: pointer;
                margin-top: 8px;
                padding: 0;
            }

            /* Ratings */
            .pd-ratings-summary { display: flex; align-items: center; gap: 8px; margin-bottom: 16px; }
            .pd-star-input { display: flex; flex-direction: row-reverse; justify-content: flex-end; }
            .pd-star-input input { display: none; }
            .pd-star-input label { font-size: 24px; color: #ccc; cursor: pointer; transition: color 0.2s; }
            .pd-star-input input:checked ~ label, .pd-star-input label:hover, .pd-star-input label:hover ~ label { color: #f5c518; }

            /* Comments */
            .pd-comment-list { display: flex; flex-direction: column; gap: 16px; margin-bottom: 24px; }
            .pd-comment { border-left: 3px solid var(--pd-border-color); padding-left: 12px; }
            .pd-comment-user { font-weight: 700; }
            .pd-comment-form textarea { width: 100%; min-height: 60px; border-radius: 8px; border: 1px solid var(--pd-border-color); padding: 10px; margin-bottom: 8px; }
            .pd-comment-form button { background-color: var(--pd-primary-blue); color: white; border: none; padding: 10px 16px; border-radius: 8px; cursor: pointer; }

            /* Related Episodes */
            .pd-related-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 16px; }
            .pd-related-card { text-decoration: none; color: inherit; }
            .pd-related-card img { width: 100%; aspect-ratio: 1/1; object-fit: cover; border-radius: 8px; margin-bottom: 8px; }
            .pd-related-card-title { font-size: 14px; font-weight: 600; }

            /* Video Player */
            .pd-video-player-wrapper {
                position: fixed;
                top: 0; left: 0; width: 100%; height: 100%;
                background: black;
                z-index: 3000;
                display: none;
                align-items: center;
                justify-content: center;
            }
            .pd-video-player-wrapper.visible { display: flex; }
            .pd-video-player-wrapper video { max-width: 100%; max-height: 100%; }

            /* Audio Mini Player */
            .pd-audio-mini-player {
                position: fixed;
                bottom: 0; left: 0; right: 0;
                background: #222;
                color: white;
                padding: 12px 16px;
                display: flex;
                align-items: center;
                gap: 16px;
                z-index: 2500;
                transform: translateY(100%);
                transition: transform 0.3s ease-in-out;
            }
            .pd-audio-mini-player.visible { transform: translateY(0); }
            .mini-player-controls button { background: none; border: none; color: white; cursor: pointer; }
            .mini-player-info { flex-grow: 1; text-overflow: ellipsis; white-space: nowrap; overflow: hidden; }
            .mini-player-progress { position: absolute; top: 0; left: 0; height: 3px; background: var(--pd-primary-blue); width: 0%; }

            /* Responsive */
            @media (min-width: 768px) {
                .pd-header, .pd-section {
                    max-width: 800px;
                    margin-left: auto;
                    margin-right: auto;
                }
                .pd-header { padding: 0; }
                .pd-section { margin-top: 24px; margin-bottom: 24px; }
                .pd-hero { height: 60vh; }
            }
        `;
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = css;
        document.head.appendChild(style);
    }

    render() {
        const { title, image, duration, releaseDate, views, description, ratings, comments, related, audioUrl, videoUrl } = this.podcast;
        const relatedPodcasts = getPodcastsByIds(related || []);

        const icons = {
            back: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>`,
            share: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path><polyline points="16 6 12 2 8 6"></polyline><line x1="12" y1="2" x2="12" y2="15"></line></svg>`,
            heart: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>`,
            download: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>`,
            play: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="0"><path d="M8 5v14l11-7z"></path></svg>`,
            video: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="0"><path d="M10 8.64L15.27 12 10 15.36V8.64M21.6 6.49A2.5 2.5 0 0 0 19.6 5a2.5 2.5 0 0 0-1.5.45L13 8.5V7a2 2 0 0 0-2-2H3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-1.5l5.1 3.05a2.5 2.5 0 0 0 1.5.45 2.5 2.5 0 0 0 2-2V7.5a2.5 2.5 0 0 0-.4-1.01z"></path></svg>`,
            eye: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>`,
            star: `★`,
            pause: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"></path></svg>`,
            close: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path></svg>`
        };

        this.element.innerHTML = `
            <div class="podcast-page-view">
                <!-- Sticky Header -->
                <header class="pd-header">
                    <button class="pd-back-btn" aria-label="Go back">${icons.back} Back</button>
                    <h1 class="pd-header-title">${title}</h1>
                </header>

                <!-- Scrollable Content -->
                <main class="pd-scroll-content">
                    <!-- Hero Section -->
                    <section class="pd-hero">
                        <img src="${image}" alt="${title}" class="pd-hero-image">
                        <div class="pd-hero-buttons">
                            <button class="pd-play-btn primary ${!audioUrl ? 'hidden' : ''}" data-action="play-audio">${icons.play} Play Audio</button>
                            <button class="pd-play-btn secondary ${!videoUrl ? 'hidden' : ''}" data-action="play-video">${icons.video} Play Video</button>
                        </div>
                        <div class="pd-hero-meta">
                            <span>${duration}</span> • <span>${releaseDate}</span> • <span style="display: flex; align-items: center; gap: 4px;">${icons.eye} ${views.toLocaleString()}</span>
                        </div>
                    </section>

                    <!-- Quick Actions -->
                    <section class="pd-quick-actions">
                        <button class="pd-action-btn" aria-label="Download">${icons.download}<span>Download</span></button>
                        <button class="pd-action-btn" aria-label="Share">${icons.share}<span>Share</span></button>
                        <button class="pd-action-btn icon-heart" aria-label="Save to favorites">${icons.heart}<span>Save</span></button>
                    </section>

                    <!-- Description -->
                    <section class="pd-section">
                        <h2 class="pd-section-title">Description</h2>
                        <div class="pd-description-text collapsed">${description.replace(/\n/g, '<br>')}</div>
                        <button class="pd-description-toggle">Show more</button>
                    </section>

                    <!-- Ratings -->
                    <section class="pd-section">
                        <h2 class="pd-section-title">Ratings</h2>
                        <div class="pd-ratings-summary">
                            <strong>${ratings.average} ${icons.star}</strong>
                            <span>(${ratings.count} ratings)</span>
                        </div>
                        <div class="pd-star-input">
                            ${[5,4,3,2,1].map(i => `
                                <input type="radio" id="star${i}" name="rating" value="${i}" /><label for="star${i}">${icons.star}</label>
                            `).join('')}
                        </div>
                    </section>

                    <!-- Comments -->
                    <section class="pd-section">
                        <h2 class="pd-section-title">Comments (${comments.length})</h2>
                        <div class="pd-comment-list">
                            ${comments.map(c => `
                                <div class="pd-comment">
                                    <p class="pd-comment-user">${c.user}</p>
                                    <p>${c.text}</p>
                                </div>
                            `).join('') || '<p>No comments yet.</p>'}
                        </div>
                        <form class="pd-comment-form">
                            <textarea placeholder="Add a comment..." required></textarea>
                            <button type="submit">Submit</button>
                        </form>
                    </section>

                    <!-- Related Episodes -->
                    <section class="pd-section">
                        <h2 class="pd-section-title">Related Episodes</h2>
                        <div class="pd-related-grid">
                            ${relatedPodcasts.map(p => `
                                <a href="#" class="pd-related-card" data-podcast-id="${p.id}">
                                    <img src="${p.image}" alt="${p.title}">
                                    <h3 class="pd-related-card-title">${p.title}</h3>
                                </a>
                            `).join('')}
                        </div>
                    </section>
                </main>

                <!-- Video Player -->
                <div class="pd-video-player-wrapper">
                    <!-- Player will be injected here by playVideo() -->
                </div>

                <!-- Audio Player -->
                <audio class="pd-audio-player" src="${audioUrl}"></audio>
                
                <!-- Mini Audio Player -->
                <div class="pd-audio-mini-player">
                    <div class="mini-player-progress"></div>
                    <div class="mini-player-info">
                        <strong>${title}</strong>
                    </div>
                    <div class="mini-player-controls">
                        <button data-action="play-pause" aria-label="Play/Pause">${icons.play}</button>
                        <button data-action="close" aria-label="Close player">${icons.close}</button>
                    </div>
                </div>
            </div>
        `;
    }

    attachEventListeners() {
        const backBtn = this.element.querySelector('.pd-back-btn');
        backBtn?.addEventListener('click', () => this.onBack());

        // Favorite/Save buttons
        this.element.querySelectorAll('.icon-heart').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.currentTarget.classList.toggle('filled');
                const isFilled = e.currentTarget.classList.contains('filled');
                e.currentTarget.setAttribute('aria-pressed', isFilled);
            });
        });

        // Description toggle
        const descToggle = this.element.querySelector('.pd-description-toggle');
        const descText = this.element.querySelector('.pd-description-text');
        if (descToggle && descText) {
            // Only show toggle if text is overflowing
            if (descText.scrollHeight > descText.clientHeight) {
                descToggle.addEventListener('click', () => {
                    const isCollapsed = descText.classList.toggle('collapsed');
                    descToggle.textContent = isCollapsed ? 'Show more' : 'Show less';
                });
            } else {
                descToggle.style.display = 'none';
            }
        }

        // Play buttons
        this.element.querySelector('[data-action="play-audio"]')?.addEventListener('click', () => this.playAudio());
        this.element.querySelector('[data-action="play-video"]')?.addEventListener('click', () => this.playVideo());

        // Mini player controls
        this.miniPlayer = this.element.querySelector('.pd-audio-mini-player');
        this.miniPlayer.querySelector('[data-action="play-pause"]')?.addEventListener('click', () => this.toggleAudioPlayback());
        this.miniPlayer.querySelector('[data-action="close"]')?.addEventListener('click', () => this.closeAudioPlayer());

        // Video player
        this.videoPlayerWrapper = this.element.querySelector('.pd-video-player-wrapper');
        this.videoPlayerWrapper.addEventListener('click', (e) => {
            if (e.target === this.videoPlayerWrapper) {
                this.closeVideoPlayer();
            }
        });
    }

    // --- Audio Player Logic ---
    playAudio() {
        if (!this.audioPlayer) {
            this.audioPlayer = this.element.querySelector('.pd-audio-player');
            this.audioPlayer.addEventListener('timeupdate', () => this.updateAudioProgress());
            this.audioPlayer.addEventListener('play', () => this.updatePlayPauseIcon(true));
            this.audioPlayer.addEventListener('pause', () => this.updatePlayPauseIcon(false));
        }
        this.miniPlayer.classList.add('visible');
        this.audioPlayer.play();
    }

    toggleAudioPlayback() {
        if (this.audioPlayer.paused) {
            this.audioPlayer.play();
        } else {
            this.audioPlayer.pause();
        }
    }

    updatePlayPauseIcon(isPlaying) {
        const btn = this.miniPlayer.querySelector('[data-action="play-pause"]');
        const icons = {
            play: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"></path></svg>`,
            pause: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"></path></svg>`
        };
        if (btn) {
            btn.innerHTML = isPlaying ? icons.pause : icons.play;
        }
    }

    updateAudioProgress() {
        if (this.audioPlayer && this.audioPlayer.duration) {
            const progressEl = this.miniPlayer.querySelector('.mini-player-progress');
            const progress = (this.audioPlayer.currentTime / this.audioPlayer.duration) * 100;
            progressEl.style.width = `${progress}%`;
        }
    }

    closeAudioPlayer() {
        this.audioPlayer.pause();
        this.miniPlayer.classList.remove('visible');
    }

    // --- Video Player Logic ---
    playVideo() {
        if (!this.podcast.videoUrl || this.podcast.videoType !== 'youtube') {
            if (this.podcast.videoUrl) {
                console.warn(`Cannot play video for podcast "${this.podcast.title}". Video type is "${this.podcast.videoType}", but only "youtube" is supported.`);
            }
            return;
        }

        this.videoPlayerWrapper.innerHTML = ''; // Clear previous player
        const iframe = document.createElement('iframe');
        // Add autoplay=1 to the URL to start the video immediately
        const videoSrc = this.podcast.videoUrl.includes('?') 
            ? `${this.podcast.videoUrl}&autoplay=1` 
            : `${this.podcast.videoUrl}?autoplay=1`;

        iframe.setAttribute('src', videoSrc);
        iframe.setAttribute('title', 'YouTube video player');
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
        iframe.setAttribute('allowfullscreen', '');
        
        // Responsive styling for the iframe
        iframe.style.width = '90%';
        iframe.style.aspectRatio = '16 / 9';
        iframe.style.maxWidth = '1280px';
        iframe.style.border = 'none';

        this.videoPlayerWrapper.appendChild(iframe);
        this.videoPlayer = iframe; // Store reference

        this.videoPlayerWrapper.classList.add('visible');
    }

    closeVideoPlayer() {
        this.videoPlayerWrapper.classList.remove('visible');
        // Stop playback by clearing the content, which removes the iframe/video element
        this.videoPlayerWrapper.innerHTML = ''; 
        this.videoPlayer = null;

        if (document.fullscreenElement) {
            document.exitFullscreen();
        }
    }
    destroy() {
        // Clean up when the component is removed
        if (this.audioPlayer) {
            this.audioPlayer.pause();
            this.audioPlayer.src = '';
        }
        this.closeVideoPlayer(); // Also removes listeners
        this.element.innerHTML = '';
    }
}